import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useCrmNotes, CrmNote } from '../crm-notes/useData';
import { moduleConfig } from '../crm-notes/config';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { usePagination } from '../../hooks/usePagination';
import { useSettings } from '../../contexts/SettingsContext';
import { PlusIcon, SpinnerIcon } from '../../components/ui/Icons';
import { Badge } from '../../components/ui';
import { cn } from '../../lib/utils';

/**
 * Cardboard View for Notes – sticky-note style layout.
 * Custom design: paper-like cards with status accent, clear hierarchy, and compact meta.
 */
const CardView: React.FC = () => {
  const { colors, theme } = useTheme();
  const { showError, showSuccess } = useNotification();
  const { appConfig } = useSettings();
  const { data = [], isLoading, mutate } = useCrmNotes();
  const [statusFilter, setStatusFilter] = useState<string>(
    moduleConfig.cardView.statusFilter?.defaultFilter ?? 'all'
  );
  const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set());
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const filteredItems = useMemo(() => {
    if (!moduleConfig.cardView.statusFilter?.enabled || statusFilter === 'all') return data;
    return data.filter(
      (item) => moduleConfig.cardView.statusFilter!.getStatus(item) === statusFilter
    );
  }, [data, statusFilter]);

  const { paginationProps, paginatedData } = usePagination({
    totalItems: filteredItems.length,
    initialItemsPerPage: appConfig.table.defaultPageSize,
  });
  const paginatedItems = useMemo(() => paginatedData(filteredItems), [filteredItems, paginatedData]);

  const handleStatusChange = async (itemId: number, newStatus: string) => {
    if (!moduleConfig.cardView.updateStatus) return;
    setUpdatingIds((prev) => new Set(prev).add(itemId));
    try {
      await moduleConfig.cardView.updateStatus!(itemId, newStatus);
      mutate();
    } catch (err: unknown) {
      showError?.(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const handleDelete = async (itemId: number) => {
    if (!moduleConfig.delete?.handler) return;
    const ok = window.confirm(moduleConfig.delete.message);
    if (!ok) return;
    setDeletingId(itemId);
    try {
      await moduleConfig.delete.handler(itemId);
      showSuccess?.('Note deleted.');
      mutate();
    } catch (err: unknown) {
      showError?.(moduleConfig.delete?.errorMessage ?? 'Failed to delete note');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString?: string) =>
    dateString ? moduleConfig.utils.formatDate(dateString) : '—';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <div className="text-center">
          <SpinnerIcon className="w-12 h-12 text-indigo-600 mx-auto" />
          <p className={cn('mt-4 text-sm', colors.textMuted)}>Loading notes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {moduleConfig.cardView.statusFilter?.enabled && (
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn('text-xs font-medium', colors.textMuted)}>Status</span>
              <div
                className={cn('inline-flex rounded-lg p-1 gap-0.5 border', colors.border, colors.surface)}
                role="group"
                aria-label="Filter notes"
              >
                {moduleConfig.cardView.statusFilter.options.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setStatusFilter(opt.value)}
                    className={cn(
                      'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                      statusFilter === opt.value
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : cn(colors.text, colors.surfaceHover, 'hover:bg-gray-100 dark:hover:bg-gray-700')
                    )}
                    aria-pressed={statusFilter === opt.value}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <span className={cn('text-sm', colors.textMuted)}>
            {filteredItems.length} {filteredItems.length === 1 ? 'note' : 'notes'}
            {paginationProps.totalPages > 1 &&
              ` · Page ${paginationProps.currentPage} of ${paginationProps.totalPages}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={moduleConfig.module.route.list}
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
              colors.border,
              colors.surfaceHover,
              'hover:bg-gray-100 dark:hover:bg-gray-700'
            )}
            title="List View"
            aria-label="List View"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </Link>
          <Link
            to={moduleConfig.module.route.new}
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
              colors.primary,
              colors.primaryText,
              'hover:opacity-90'
            )}
            title="New note"
            aria-label="New note"
          >
            <PlusIcon className="h-5 w-5" />
          </Link>
        </div>
      </div>

      {/* Cards */}
      {filteredItems.length === 0 ? (
        <div className="flex items-center justify-center min-h-[280px]">
          <p className={cn('text-base', colors.textMuted)}>
            {statusFilter !== 'all'
              ? moduleConfig.emptyStates.noFilterResults
              : moduleConfig.emptyStates.noData}
          </p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 items-start overflow-y-auto pb-4 content-start">
            {paginatedItems.map((note) => {
              const id = moduleConfig.cardView.getId(note);
              const isUpdating = updatingIds.has(id);
              const isDeleting = deletingId === id;
              const isArchived = !!note.noteArchived;
              const title = moduleConfig.cardView.getTitle(note);
              const fromName = note.noteFromUser
                ? [note.noteFromUser.userFirstName, note.noteFromUser.userLastName].filter(Boolean).join(' ').trim()
                : '';
              const toName = note.noteToUser
                ? [note.noteToUser.userFirstName, note.noteToUser.userLastName].filter(Boolean).join(' ').trim()
                : '';
              const created = formatDate(note.noteCreatedAt);

              return (
                <div
                  key={id}
                  className={cn(
                    'relative rounded-xl border-l-4 shadow-sm transition-all hover:shadow-md',
                    'flex flex-col min-h-0 overflow-hidden',
                    theme === 'dark' ? 'bg-gray-800/80 border-gray-600' : 'bg-amber-50/60 border-amber-400',
                    isArchived && (theme === 'dark' ? 'bg-gray-800/50 border-gray-500' : 'bg-gray-50 border-gray-300'),
                    (isUpdating || isDeleting) && 'opacity-60 pointer-events-none'
                  )}
                >
                  {/* Status pill + spacer */}
                  <div className="flex items-start justify-between gap-2 px-3 pt-2 pb-0.5">
                    <div className="flex-1 min-w-0" />
                    <Badge variant={isArchived ? 'warning' : 'success'} className="text-[11px]">
                      {isArchived ? 'Archived' : 'Active'}
                    </Badge>
                  </div>

                  {/* Note content */}
                  <Link
                    to={moduleConfig.module.route.detail(id)}
                    className={cn(
                      'flex-1 px-3 py-1 block min-h-0',
                      'text-sm leading-snug line-clamp-2',
                      colors.text,
                      'hover:underline decoration-indigo-500 decoration-2 underline-offset-2'
                    )}
                  >
                    {title}
                  </Link>

                  {/* Meta: client, from→to, date */}
                  <div className={cn('px-3 py-1 text-xs', colors.textMuted)}>
                    {note.client?.clientName && (
                      <span className="font-medium">{note.client.clientName}</span>
                    )}
                    {(fromName || toName || created) && (
                      <span className="block truncate">
                        {fromName && toName && `${fromName} → ${toName}`}
                        {fromName && !toName && `From ${fromName}`}
                        {!fromName && toName && `To ${toName}`}
                        {created && (fromName || toName ? ` · ${created}` : created)}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div
                    className={cn(
                      'flex items-center justify-end gap-0.5 px-3 py-1.5 border-t',
                      theme === 'dark' ? 'border-gray-700' : 'border-amber-200/60'
                    )}
                  >
                    <Link
                      to={moduleConfig.module.route.detail(id)}
                      onClick={(e) => e.stopPropagation()}
                      className={cn(
                        'p-1.5 rounded-md transition-colors',
                        colors.textMuted,
                        'hover:bg-indigo-100 hover:text-indigo-700 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-300'
                      )}
                      title="View"
                      aria-label="View"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </Link>
                    <Link
                      to={moduleConfig.module.route.edit(id)}
                      onClick={(e) => e.stopPropagation()}
                      className={cn(
                        'p-1.5 rounded-md transition-colors',
                        colors.textMuted,
                        'hover:bg-indigo-100 hover:text-indigo-700 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-300'
                      )}
                      title="Edit"
                      aria-label="Edit"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(id, isArchived ? 'active' : 'archived');
                      }}
                      disabled={isUpdating}
                      className={cn(
                        'p-2 rounded-lg transition-colors',
                        colors.textMuted,
                        'hover:bg-amber-100 hover:text-amber-700 dark:hover:bg-amber-900/30 dark:hover:text-amber-300'
                      )}
                      title={isArchived ? 'Restore' : 'Archive'}
                      aria-label={isArchived ? 'Restore' : 'Archive'}
                    >
                      {isUpdating ? (
                        <SpinnerIcon className="w-4 h-4" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(id);
                      }}
                      disabled={isDeleting}
                      className={cn(
                        'p-1.5 rounded-md transition-colors',
                        colors.textMuted,
                        'hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900/30 dark:hover:text-red-300'
                      )}
                      title="Delete"
                      aria-label="Delete"
                    >
                      {isDeleting ? (
                        <SpinnerIcon className="w-4 h-4" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {filteredItems.length > 0 && paginationProps.totalPages > 1 && (
            <div
              className={cn(
                'flex-shrink-0 flex flex-wrap items-center justify-between gap-4 py-4 border-t',
                colors.border
              )}
            >
              <p className={cn('text-sm', colors.textMuted)}>
                Showing {(paginationProps.currentPage - 1) * paginationProps.itemsPerPage + 1}–
                {Math.min(paginationProps.currentPage * paginationProps.itemsPerPage, paginationProps.totalItems)} of{' '}
                {paginationProps.totalItems}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => paginationProps.onPageChange(paginationProps.currentPage - 1)}
                  disabled={paginationProps.currentPage === 1}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50',
                    colors.border,
                    colors.surface,
                    colors.text,
                    'hover:bg-gray-100 dark:hover:bg-gray-700'
                  )}
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => paginationProps.onPageChange(paginationProps.currentPage + 1)}
                  disabled={paginationProps.currentPage === paginationProps.totalPages}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50',
                    colors.border,
                    colors.surface,
                    colors.text,
                    'hover:bg-gray-100 dark:hover:bg-gray-700'
                  )}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CardView;
